import React, { useEffect, useState, useMemo } from "react";
import {
  Backdrop,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  Button,
} from "@mui/material";
import FunctionsData from "../../../Functions/FunctionsData";
import StaffVouchersShowFilters from "./StaffVouchersShowFilters";
import StaffVouchersShowTableRow from "./StaffVouchersShowTableRow";
import StaffVoucherCancelMultiple from "./StaffVoucherCancelMultiple";

export default function StaffVouchers({
  isOpen,
  setIsOpen,
  row,
  version,
  setVersion,
}) {
  const controllerToAbort = new AbortController();
  const controllerToAbortCampaign = new AbortController();

  const [loading, setLoading] = useState(true);
  const [loadingVouchers, setLoadingVouchers] = useState(true);
  const [data, setData] = useState([]);
  const [dataFiltered, setDataFiltered] = useState([]);
  const [filter, setFilter] = useState("");
  const [hideActioned, setHideActioned] = useState(true);
  const [from, setFrom] = useState(false);
  const [to, setTo] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [searchCampaign, setSearchCampaign] = useState("all");
  const [sortVal, setSortVal] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchName, setSearchName] = useState("all");

  const [showRedeemed, setShowRedeemed] = useState(true);
  const [showUnredeemed, setShowUnredeemed] = useState(true);

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRows, setTotalRows] = useState(0);

  // For multiple voucher selection when version === "unused"
  const [selectedVouchers, setSelectedVouchers] = useState([]); // Initialized as an empty array
  const [selectAll, setSelectAll] = useState(false);
  const [showCancelMultiple, setShowCancelMultiple] = useState(false);

  // Calculate total pages based on totalRows
  const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
  const startRow = (page - 1) * rowsPerPage + 1;
  const endRow = Math.min(page * rowsPerPage, totalRows);

  const handleChangePage = (event, newPage) => {
    if (newPage > totalPages || newPage < 1) return;
    setPage(newPage);
    setLoadingVouchers(true);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
    setLoadingVouchers(true);
  };

  const handleSort = (columnId) => {
    if (sortVal === columnId) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortVal(columnId);
      setSortOrder("asc");
    }
    setPage(1);
    setLoadingVouchers(true);
  };

  useEffect(() => {
    FunctionsData.fetchDataGet(
      FunctionsData.getApiUrl("/api/campaign/combined"),
      setCampaigns,
      true,
      setLoading,
      controllerToAbortCampaign
    );
    return () => {
      controllerToAbortCampaign.abort();
    };
  }, []);

  useEffect(() => {
    try {
      setSearchName(
        searchCampaign !== "all"
          ? campaigns.find(
              ({ id }) => String(id) === String(searchCampaign)
            )?.name
          : "all"
      );
    } catch (e) {
      setSearchName("all");
    }

    const buildUrl = () => {
      let url = `/api/merco/all/${searchCampaign}/?page=${page}&limit=${rowsPerPage}&actioned=${
        hideActioned ? 1 : 0
      }&from=${from}&to=${to}&sort=${sortVal}&order=${sortOrder}&filter=${filter}`;
      if (version === "unused") {
        url += "&marked_unused=1";
      }
      if (showRedeemed && !showUnredeemed && version !== "unused") {
        url += "&redeem=1&unredeem=0";
      } else if (showRedeemed && !showUnredeemed && version === "unused") {
        url += "&redeem=0&unredeem=0";
      } else if (!showRedeemed && showUnredeemed) {
        url += "&redeem=0&unredeem=1";
      } else if (showRedeemed && showUnredeemed && version === "unused") {
        url += "&redeem=0&unredeem=1";
      } else if (showRedeemed && showUnredeemed && version !== "unused") {
        url += "&redeem=1&unredeem=1";
      } else {
        url += "&redeem=0&unredeem=0";
      }
      return FunctionsData.getApiUrl(url);
    };

    const url = buildUrl();
    FunctionsData.fetchDataGet(url, setData, true, setLoadingVouchers, controllerToAbort);

    return () => {
      controllerToAbort.abort();
    };
  }, [
    campaigns,
    page,
    rowsPerPage,
    showRedeemed,
    showUnredeemed,
    hideActioned,
    loadingVouchers,
    from,
    to,
    sortVal,
    sortOrder,
    filter,
    searchCampaign,
    version,
  ]);

  useEffect(() => {
    if (data && data.length > 0) {
      setTotalRows(data[0]["data_all"]);
    } else {
      setTotalRows(0);
    }
    setDataFiltered(data || []); // Ensure dataFiltered is an array
    setLoadingVouchers(false);
  }, [data]);

  const columnsRedeemed = [
    { id: "status", label: "Status" },
    { id: "voucher_nr", label: "Voucher #" },
    { id: "voucher_paid_status", label: "Payment Status" },
    { id: "created_at", label: "Issued" },
    { id: "transaction_date", label: "Redeemed" },
    { id: "a_date_surgery", label: "Procedure" },
    { id: "supplier_business_name", label: "Vet name" },
    { id: "animal_species", label: "Animal details" },
    { id: "owner_name", label: "Owner" },
  ];

  const columnsIssued = [
    { id: "status", label: "Status" },
    { id: "voucher_nr", label: "Voucher #" },
    { id: "voucher_paid_status", label: "Payment Status" },
    { id: "created_at", label: "Issued" },
    { id: "supplier_business_name", label: "Vet name" },
    { id: "animal_species", label: "Animal details" },
    { id: "contact_name", label: "Contact" },
  ];

  const actionsColumn = { id: "actions", label: "" };

  const columns = useMemo(() => {
    let columnsArray = [];
    if (showRedeemed && !showUnredeemed) {
      columnsArray = columnsRedeemed;
    } else if (!showRedeemed && showUnredeemed) {
      columnsArray = columnsIssued;
    } else if (showRedeemed && showUnredeemed) {
      const combinedColumns = [];
      const seen = new Set();
      const allColumns = [...columnsRedeemed, ...columnsIssued];
      allColumns.forEach((col) => {
        if (!seen.has(col.id)) {
          seen.add(col.id);
          combinedColumns.push(col);
        }
      });
      columnsArray = combinedColumns;
    }
    if (columnsArray.length > 0) {
      columnsArray.push(actionsColumn);
    }
    return columnsArray;
  }, [showRedeemed, showUnredeemed]);

  // When selecting a row, use the consistent unique id
  const handleSelectRow = (voucherId, isSelected) => {
    setSelectedVouchers((prev) =>
      isSelected ? [...prev, voucherId] : prev.filter((v) => v !== voucherId)
    );
  };

  // Use the same unique id here as well (voucher_id or fallback to id)
  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    const ids = checked
      ? dataFiltered
          .filter((row) => row.is_cancelled !== 1)
          .map((row) => row.voucher_id || row.id)
      : [];
    console.log("handleSelectAll - Selected IDs:", ids);
    setSelectedVouchers(ids);
  };
  

  const handleCancelVouchers = () => {
    setShowCancelMultiple(true);
  };

  if (loading || loadingVouchers)
    return (
      <div
        style={{
          padding: 20,
          width: "calc(100% - 40px)",
          boxShadow:
            "rgba(50, 50, 93, 0.25) 0px 6px 12px -2px, rgba(0, 0, 0, 0.3) 0px 3px 7px -3px",
        }}
      >
        Loading vouchers for campaign <b>{searchName}</b>, stay tuned ...
      </div>
    );

  return (
    <>
      <Backdrop open={loadingVouchers} style={{ zIndex: 1000 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <StaffVouchersShowFilters
        setPage={setPage}
        fromParent={from}
        setFrom={setFrom}
        toParent={to}
        setTo={setTo}
        campaigns={campaigns}
        searchCampaign={searchCampaign}
        setSearchCampaign={setSearchCampaign}
        hideActioned={hideActioned}
        setHideActioned={setHideActioned}
        sortVal={sortVal}
        setSortVal={setSortVal}
        filter={filter}
        setFilter={setFilter}
        showRedeemed={showRedeemed}
        setShowRedeemed={setShowRedeemed}
        showUnredeemed={showUnredeemed}
        setShowUnredeemed={setShowUnredeemed}
        version={version}
      />
      <hr />
      {version === "unused" && (
        <div
          style={{
            marginBottom: "1rem",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Checkbox checked={selectAll} onChange={handleSelectAll} />
          <span>Select All</span>
          <Button
            variant="contained"
            onClick={handleCancelVouchers}
            disabled={selectedVouchers.length === 0}
            sx={{ marginLeft: "1rem" }}
          >
            Cancel Vouchers
          </Button>
        </div>
      )}
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              {version === "unused" && (
                <TableCell padding="checkbox">
                  <Checkbox checked={selectAll} onChange={handleSelectAll} />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  onClick={() => handleSort(column.id)}
                  style={{ cursor: "pointer" }}
                >
                  {column.label}
                  {sortVal === column.id && column.id !== "actions" && (
                    <span style={{ marginLeft: 8 }}>
                      {sortOrder === "asc" ? "▲" : "▼"}
                    </span>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {dataFiltered && dataFiltered.length > 0 ? (
              dataFiltered.map((row) => {
                const redeem = row.is_redeemed > 0;
                // Use the consistent unique id for each row
                const uniqueId = row.voucher_id || row.id;
                return (
                  <StaffVouchersShowTableRow
                    row={row}
                    key={row.id}
                    redeem={redeem}
                    setLoadingVouchers={setLoadingVouchers}
                    hideActioned={hideActioned}
                    columns={columns}
                    version={version}
                    selected={
                      version === "unused"
                        ? selectedVouchers.includes(uniqueId)
                        : false
                    }
                    onSelectRow={handleSelectRow}
                  />
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} style={{ textAlign: "center" }}>
                  No data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: 20,
        }}
      >
        <FormControl
          variant="outlined"
          size="small"
          style={{ minWidth: 120, marginRight: 20 }}
        >
          <InputLabel id="rows-per-page-select-label">Rows per page</InputLabel>
          <Select
            labelId="rows-per-page-select-label"
            id="rows-per-page-select"
            value={rowsPerPage}
            onChange={handleChangeRowsPerPage}
            label="Rows per page"
          >
            {[10, 25, 50, 100].map((rows) => (
              <MenuItem key={rows} value={rows}>
                {rows}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <div>
          {totalRows > 0 ? `${startRow}-${endRow} of ${totalRows}` : "0 results"}
        </div>
        <Pagination count={totalPages} page={page} onChange={handleChangePage} shape="rounded" />
      </div>
      {showCancelMultiple && (
        <StaffVoucherCancelMultiple
          open={showCancelMultiple}
          setOpen={setShowCancelMultiple}
          vouchers={selectedVouchers}
          setLoadingVouchers={setLoadingVouchers}
          setSelectedVouchers={setSelectedVouchers}
          setSelectAll={setSelectAll}
        />
      )}
    </>
  );
}
